import { useSelector } from 'react-redux'
import { getAccount } from 'state/Account'
import { getUser } from 'state/Auth'
import { Alerts } from 'utils'
import './styles.scss'

import HelpScout from './platforms/HelpScout'
import Intercom from './platforms/Intercom'
//we could lazy-import the chat platforms, but native can't really do it without some shenanigans (like t9n files)

//stored in `window` so it stays only during the current reload, but outlives re-renders
window.isChatOpen = window.isChatOpen || false

export default class SupportChat {

	account = null
	user = null

	//FIXME somehow, use constants here... maybe create something like Alert.warning() to access the store without hooks
	static #platforms = {
		1: () => Intercom,
		// 2: () => LiveChat,
		3: () => HelpScout,
	}

	/** @return {ChatPlatform} */
	#platform = () => {
		if (!this.#platform.instance) {
			this.#platform.instance = new (SupportChat.#platforms[this.account.chat.platform]())(this.account, this.user)
		}
		return this.#platform.instance
	}

	/** @return {'LiveChat'|'Intercom'|'HelpScout'} */
	get platformName() { return this.#platform().name }

	/**
	 * Somewhat equivalent to a selector hook that instantiates the SupportChat object for you.
	 * @see {@link SupportChat.prepare()} is the actual place where the instance is created.
	 * @type {function(): ?SupportChat}
	 */
	static use = useSupportChat

	/**
	 * Sibling of {@link prepare()}, to be used inside Redux code.
	 * @param state
	 * @return {?SupportChat}
	 * @fixme called a bunch of times, even logout... it seems a re-render happens with each state clear in the logout??
	 */
	static fromState(state) {
		const account = getAccount(state)
		const user    = getUser(state) //the user might be gone with a faulty auth state
		return account?.chat && user.isValid? SupportChat.prepare(account, user) : null
	}

	/**
	 * Sort of a factory/constructor for this class, so we verify params before actually instantiating an object.
	 * @constructor
	 * @see {@link SupportChat.use()} - you probably want to use the "hook" instead of feeding state directly here.
	 * @param account {Account}
	 * @param user {User}
	 * @return {?SupportChat} Returns false if the account has no support for Chat, or their platform is not implemented.
	 */
	static prepare(account, user) {
		if (!account.chat) {
			console.warn('Trying to configure support chat without that being enabled in the account (missing `chat` key).', account)
			return null
		}

		if (!(account.chat.platform in SupportChat.#platforms)) {
			console.warn(`Chat platform '${account.chat.platform}' not implemented yet`)
			return null
		}

		const me   = new SupportChat()
		me.account = account
		me.user    = user

		if (!me.#platform().isUp && me.#platform().initOnStartup) {
			me.init({ bg: true })
		}

		return me
	}

	/**
	 * If the current platform supports a given feature.
	 * @param feature {SupportChat.FEAT_EVENTS|SupportChat.FEAT_PAGE_VIEWS}
	 */
	supports = feature => this.#platform().supportedFeatures & feature

	/**
	 * @param passive {boolean} Passive operations don't check the current platform availability - e.g. events can still be sent if the platform got disabled during normal app usage.
	 * @param bg {boolean} Background operations - won't cause alerts.
	 * @return {Promise<boolean>}
	 */
	async init({ bg = false } = {}) {
		console.debug('initializing chat')
		if (this.#platform().isUp) { //sanity check
			console.debug('already initialized')
			return true
		}

		if (this.account.chat) {
			console.debug('initializing!')
			return this.#platform().init()
		} else {
			console.debug('no data to initialize chat????')
			if (!bg) {
				Alerts.warning('Our Support Chat is currently unavailable. In the meantime, get in touch with us via email.', { perm: true })
			}
			return false
		}
	}

	/**
	 * Controls the internal flag about the chat interface open/close state - which is used to avoid reopening the chat
	 * when it's already open, among other tasks..
	 * Should be used only by {@link ChatPlatform} subclasses where the chat interface may close without our own methods.
	 * E.g. Help Scout doesn't allow us to hide their floating button, and thus the user may close the chat through it;
	 * thus, this method must hook onto their open/close events.
	 * @param state {?boolean} If given, changes the internal open/close state to it; if not, toggles it.
	 */
	static internalToggle(state = undefined) {
		console.debug('forcing the internal toggle', state)
		if (state === undefined) {
			window.isChatOpen = !window.isChatOpen
		} else {
			window.isChatOpen = state
		}
	}

	//must return a promise, so the button can show a spinner
	toggle = async () => await this[window.isChatOpen? 'close' : 'open']()

	/**
	 * Just a boolean value to define what {@link toggle} does.
	 * Should be manually set when it's possible to show/hide the chat via its own interface.
	 * @param bool {boolean}
	 */
	static set isChatOpen(bool) { window.isChatOpen = bool }

	async close() {
		if (window.isChatOpen) {
			if (this.#platform().isUp) {
				this.#platform().close()
				console.debug('closed chat')
			}
			window.isChatOpen = false
		}
	}

	async open() {
		console.debug('request to chat')
		if (!window.isChatOpen) {
			if (await this.init()) {
				this.#platform().open()
				window.isChatOpen = true
				console.debug('chat opened')
			} else {
				console.debug('requested to open chat, but ')
			}
		} else {
			console.debug('requested to open chat, but it is already open')
		}
	}

	nuke() {
		window.isChatOpen = false
		if (this.#platform().isUp) {
			console.debug('nuking chat')
			this.#platform().nuke()
		} else {
			console.debug('no need to nuke chat')
		}
	}

	/**
	 * Uploads a user event into the current Chat platform, if it supports this feature.
	 * @param name {string} The event name - gets prepended with "[Mgr]" to distinguish from Admin events
	 * @param payload {object} Must be an object of details, you can't send a list. Nested objects may get complicated to render, if they even work.
	 * @return {Promise<void>}
	 */
	async event(name, payload = {}) {
		if (this.supports(SupportChat.FEAT_EVENTS)) {
			name = `[Mgr] ${name}` //make sure this gets differentiated from Admin events
			await this.init()
			this.#platform().event(name, payload)
		}
	}

	async sendPageView() {
		if (this.supports(SupportChat.FEAT_PAGE_VIEWS)) {
			await this.init()
			this.#platform().sendPageView()
		}
	}
}

//bitwise flags
SupportChat.FEAT_EVENTS     = 1
SupportChat.FEAT_PAGE_VIEWS = 2

/** @return {?SupportChat} */
function useSupportChat() {
	return useSelector(state => SupportChat.fromState(state), (a, b) => (
		(!a && !b) || ( //one might be false
			(a && b) &&
			a.account.chat.platform == b.account.chat.platform &&
			a.account.chat.key == b.account.chat.key &&
			((!a.user && !b.user) || (a.user.id == b.user.id))
		)
	))
}
